import React, { useEffect, useState } from "react";
import moment from "moment-timezone";
import {
  TextField,
  Button,
  CssBaseline,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Grid,
  MenuItem,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { paths } from "../../../routes/paths";
import {
  CustomTableContainer,
  CustomTableHead,
} from "../../../components/TableComponent";
import {
  fetchTimeSlots,
  fetchInterviewData,
  createTimeSlots,
  sendEmailToCandidate,
  startBatchCallAPI,
  getBatchStatusAPI,
} from "../../../services/interviewService";
import { scheduleScreeningCallAPI } from "../../../services/screeningService";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const Interview = () => {
  const { jobId } = useParams();
  // const Job = useSelector((state) => state.appreducer.selectedJob);
  // const jobId = Job ? Job["Job Id"] : null;
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [timeZone, setTimeZone] = useState(""); // Default is GMT
  const [slotloading, setslotLoading] = useState(false);
  const [loading, setLoading] = useState(false) ; 
  const [timeSlotsCreated, setTimeSlotsCreated] = useState(false);
  const [EmailsSent, setEmailsSent] = useState(false);
  const [interviewData, setInterviewData] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [timeSlotsAvailable, setTimeSlotsAvailable] = useState(false);
  const [callWindowStart, setCallWindowStart] = useState('');
  const [callWindowEnd, setCallWindowEnd] = useState('');
  const [callTimezone, setCallTimezone] = useState('UTC');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [batchError, setBatchError] = useState('');
  const [screeningLoading, setScreeningLoading] = useState(false);

  // Get time Slots if available, when component Mount.
  useEffect(() => {
    const getTimeSlots = async () => {
      try {
        const data = await fetchTimeSlots(jobId);
        const availableSlots = data.filter((slot) => !slot.booked);
        setTimeSlotsAvailable(availableSlots.length > 0);
      } catch (error) {
        console.error("Error fetching time slots:", error);
        setTimeSlotsAvailable(false);
      }
    };

    if (jobId) {
      getTimeSlots();
    }
  }, [jobId]);

  // fetch Selected Candidates Data.
  useEffect(() => {
    const getInterviewData = async () => {
      setLoading(true);
      try {
        const fetchedData = await fetchInterviewData(jobId);
        const sortedData = fetchedData.sort(
          (a, b) =>
            (b["Total Resume score"] || 0) - (a["Total Resume score"] || 0)
        );

        const rankedData = sortedData.map((candidate, index) => ({
          ...candidate,
          rank: index + 1,
          url: `${jobId}/${candidate["Resume Id"]}`,
          ChatBot: `${jobId}/${candidate["Resume Id"]}/chat`,
          resumeId: candidate["Resume Id"],
        }));

        setInterviewData(rankedData);
      } catch (error) {
        console.error("Error fetching interview data:", error);
      }finally{
        setLoading(false); 
      }
    };

    if (jobId) {
      getInterviewData();
    }
  }, [jobId]);

  // Fetch existing batch status on mount
  useEffect(() => {
    if (!jobId) return;
    getBatchStatusAPI(jobId)
      .then((status) => { if (status.active) setBatchStatus(status); })
      .catch(() => {});
  }, [jobId]);

  // Poll batch status every 60s while active
  useEffect(() => {
    if (!batchStatus || !batchStatus.active) return;
    const interval = setInterval(async () => {
      try {
        const status = await getBatchStatusAPI(jobId);
        setBatchStatus(status);
        if (!status.active) clearInterval(interval);
      } catch (_) {}
    }, 60000);
    return () => clearInterval(interval);
  }, [batchStatus?.active, jobId]);

  const handleScheduleAICalls = async () => {
    if (!callWindowStart || !callWindowEnd || !callTimezone) {
      setBatchError('Please set start time, end time, and timezone.');
      return;
    }
    setBatchLoading(true);
    setBatchError('');
    try {
      const resumeIds = interviewData.map((c) => c.resumeId);
      await startBatchCallAPI(jobId, resumeIds, callWindowStart, callWindowEnd, callTimezone);
      const status = await getBatchStatusAPI(jobId);
      setBatchStatus(status);
    } catch (err) {
      setBatchError(err.message || 'Failed to schedule AI calls.');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleScheduleScreeningCalls = async () => {
    setScreeningLoading(true);
    try {
      const resumeIds = interviewData.map((c) => c.resumeId);
      await Promise.allSettled(
        resumeIds.map((resumeId) => scheduleScreeningCallAPI(jobId, resumeId))
      );
      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "Screening calls scheduled. View results in the Screening Dashboard.",
        })
      );
      navigate(`${paths.dashboard.screening}/${jobId}`);
    } catch (error) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: error.message || "Failed to schedule screening calls.",
        })
      );
    } finally {
      setScreeningLoading(false);
    }
  };

  // Create Time Slots .
  const handleCreateTimeSlots = async () => {
    setslotLoading(true);

    const now = new Date();
    let startDateTime = `${startDate} ${startTime}`;
    let endDateTime = `${endDate} ${endTime}`;
    let nowDateTime;
    // Map timeZone values to IANA time zone names
    const timeZoneMapping = {
      IST: "Asia/Kolkata",
      GMT: "GMT",
      UTC: "UTC",
      ET: "America/New_York",
      CET: "Europe/Berlin",
      CST: "Asia/Shanghai",
      JST: "Asia/Tokyo",
      PT: "America/Los_Angeles",
      AEST: "Australia/Sydney",
      BRT: "America/Sao_Paulo",
      MSK: "Europe/Moscow",
      AST: "Asia/Riyadh",
      PST: "America/Vancouver",
    };

    const ianaTimeZone = timeZoneMapping[timeZone];

    if (ianaTimeZone) {
      // For start and end times, create moment objects in the selected timezone
      const startDateTimeInSourceTimeZone = moment.tz(
        startDateTime,
        "YYYY-MM-DD HH:mm",
        ianaTimeZone
      );
      const endDateTimeInSourceTimeZone = moment.tz(
        endDateTime,
        "YYYY-MM-DD HH:mm",
        ianaTimeZone
      );

      // For current time, create a moment object in local timezone, then convert to the selected timezone
      const nowDateTimeInSourceTimeZone = moment(now).tz(ianaTimeZone);

      // Convert all times to GMT for storage and comparison
      const startDateTimeGMT = startDateTimeInSourceTimeZone.clone().tz("GMT");
      const endDateTimeGMT = endDateTimeInSourceTimeZone.clone().tz("GMT");
      const nowDateTimeGMT = nowDateTimeInSourceTimeZone.clone().tz("GMT");

      startDateTime = startDateTimeGMT.format("YYYY-MM-DD HH:mm");
      endDateTime = endDateTimeGMT.format("YYYY-MM-DD HH:mm");
      nowDateTime = nowDateTimeGMT.format("YYYY-MM-DD HH:mm");

      console.log("Converted Start DateTime:", startDateTime);
      console.log("Converted End DateTime:", endDateTime);
      console.log("Converted Current DateTime:", nowDateTime);
    } else {
      console.error("Invalid or unsupported time zone:", timeZone);
      return;
    }

    try {
      // Parse the converted datetime strings for comparison - all are now in GMT
      const nowDate = moment(nowDateTime, "YYYY-MM-DD HH:mm");
      const startDateMoment = moment(startDateTime, "YYYY-MM-DD HH:mm");
      const endDateMoment = moment(endDateTime, "YYYY-MM-DD HH:mm");

      if (endDateMoment.isBefore(startDateMoment)) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: "End date must be after the start date.",
          })
        );
        return;
      }

      if (endTime <= startTime) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: "End time must be after start time.",
          })
        );
        return;
      }

      if (
        startDateMoment.isBefore(nowDate) ||
        endDateMoment.isBefore(nowDate)
      ) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: "Start or End dates cannot be in the past.",
          })
        );
        return;
      }

      const payload = {
        start_date: startDateTime.split(" ")[0],
        end_date: endDateTime.split(" ")[0],
        start_time: startDateTime.split(" ")[1],
        end_time: endDateTime.split(" ")[1],
      };

      await createTimeSlots(jobId, payload);

      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "Time slots created successfully!",
        })
      );

      setTimeSlotsCreated(true);
      setTimeSlotsAvailable(true);
    } catch (error) {
      console.error("Error creating time slots:", error);
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: "Failed to create time slots. Please try again.",
        })
      );
    }finally{
      setslotLoading(false);
    }
  };

  const handleSendEmails = async () => {
    try {
      const emailRequests = interviewData.map((candidate) =>
        sendEmailToCandidate(jobId, candidate.resumeId)
      );

      await Promise.all(emailRequests);

      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "Emails sent successfully to all selected candidates!",
        })
      );
      setEmailsSent(true);
    } catch (error) {
      console.error("Error sending emails:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to send emails. Please try again.";
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: errorMessage,
        })
      );
    }
  };

  const handleViewRankings = async () => {
    // navigate(paths.dashboard.rankings, { replace: true });
    navigate(`${paths.dashboard.rankings}/${jobId}`);
  };

  // Helper for summary pills
  const totalCandidates = interviewData.length;
  const selectedCandidates = 0; // Selection logic can be added if needed

  if (!jobId) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CssBaseline />
        <Typography variant="h5">
          Select a job from Job Management Section
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header Section */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Interview Scheduling
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Box display="flex" alignItems="center" px={2} py={1} borderRadius={3} bgcolor="#F4F6F8">
            <PeopleAltIcon sx={{ color: '#1976d2', mr: 1 }} />
            <Typography fontWeight={600}>{totalCandidates} Total Candidates</Typography>
          </Box>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PhoneIcon />}
            onClick={handleScheduleScreeningCalls}
            disabled={totalCandidates === 0 || screeningLoading}
            sx={{ fontWeight: 600 }}
          >
            {screeningLoading ? 'Scheduling...' : 'Schedule Screening'}
          </Button>
        </Box>
      </Box>

      {/* Time Slot Section as Accordion */}
      <Accordion
        disabled={timeSlotsCreated || EmailsSent}
        sx={{ mb: 3, borderRadius: 3, border: '1px solid #E0E3E7', bgcolor: '#FAFAFA', boxShadow: 'none' }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="time-slot-content"
          id="time-slot-header"
        >
          <Box display="flex" alignItems="center" gap={2} width="100%" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <AccessTimeIcon sx={{ color: '#1976d2', fontSize: 32 }} />
              <Typography variant="body1" fontWeight={600}>
                Create Time Slots
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateTimeSlots}
              disabled={!startDate || !endDate || !startTime || !endTime || slotloading}
              sx={{ minWidth: 160, fontWeight: 600 }}
            >
              {slotloading ? 'Creating...' : 'Create Time Slots'}
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!slotloading ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date (DD-MM-YYYY)"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End Date (DD-MM-YYYY)"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Timezone"
                  select
                  value={timeZone}
                  onChange={(e) => setTimeZone(e.target.value)}
                >
                  <MenuItem value="UTC">UTC (Coordinated Universal Time)</MenuItem>
                  <MenuItem value="ET">ET (Eastern Time)</MenuItem>
                  <MenuItem value="GMT">GMT (Greenwich Mean Time)</MenuItem>
                  <MenuItem value="CET">CET (Central European Time)</MenuItem>
                  <MenuItem value="CST">CST (China Standard Time)</MenuItem>
                  <MenuItem value="IST">IST (Indian Standard Time)</MenuItem>
                  <MenuItem value="JST">JST (Japan Standard Time)</MenuItem>
                  <MenuItem value="PT">PT (Pacific Time)</MenuItem>
                  <MenuItem value="AEST">AEST (Australian Eastern Standard Time)</MenuItem>
                  <MenuItem value="BRT">BRT (Brasília Time)</MenuItem>
                  <MenuItem value="MSK">MSK (Moscow Standard Time)</MenuItem>
                  <MenuItem value="AST">AST (Arabian Standard Time)</MenuItem>
                  <MenuItem value="PST">PST (Pacific Standard Time)</MenuItem>
                </TextField>
              </Grid>
              {timeZone && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`Start Time (${timeZone})`}
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`End Time (${timeZone})`}
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
              <CircularProgress />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Creating time slots...
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Schedule AI Interviews Accordion */}
      <Accordion
        disabled={batchStatus?.active}
        sx={{ mb: 3, borderRadius: 3, border: '1px solid #E0E3E7', bgcolor: '#FAFAFA', boxShadow: 'none' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={2} width="100%" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <PhoneIcon sx={{ color: '#1976d2', fontSize: 32 }} />
              <Typography variant="body1" fontWeight={600}>
                Schedule AI Interviews
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              onClick={(e) => { e.stopPropagation(); handleScheduleAICalls(); }}
              disabled={interviewData.length === 0 || batchLoading || !callWindowStart || !callWindowEnd}
              sx={{ minWidth: 200, fontWeight: 600 }}
            >
              {batchLoading ? 'Scheduling...' : 'Start Scheduled Calls'}
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Call Window Start"
                type="time"
                value={callWindowStart}
                onChange={(e) => setCallWindowStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Call Window End"
                type="time"
                value={callWindowEnd}
                onChange={(e) => setCallWindowEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                margin="normal"
                label="Timezone"
                select
                value={callTimezone}
                onChange={(e) => setCallTimezone(e.target.value)}
              >
                <MenuItem value="UTC">UTC (Coordinated Universal Time)</MenuItem>
                <MenuItem value="America/New_York">ET (Eastern Time)</MenuItem>
                <MenuItem value="Europe/London">GMT (Greenwich Mean Time)</MenuItem>
                <MenuItem value="Europe/Berlin">CET (Central European Time)</MenuItem>
                <MenuItem value="Asia/Kolkata">IST (Indian Standard Time)</MenuItem>
                <MenuItem value="America/Los_Angeles">PT (Pacific Time)</MenuItem>
                <MenuItem value="Australia/Sydney">AEST (Australian Eastern Standard Time)</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          {batchError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {batchError}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Candidate Analysis Card */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E0E3E7', bgcolor: '#FAFAFA' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Candidates Selected
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalCandidates} candidates found
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {batchStatus && (
              <Box display="flex" alignItems="center" gap={1} px={2} py={1} borderRadius={3}
                bgcolor={batchStatus.status === 'completed' ? '#E8F5E9' : '#E3F2FD'}>
                {batchStatus.status === 'completed' ? (
                  <CheckCircleIcon sx={{ color: '#388E3C', fontSize: 18 }} />
                ) : (
                  <ScheduleIcon sx={{ color: '#1976d2', fontSize: 18 }} />
                )}
                <Typography variant="body2" fontWeight={600}
                  color={batchStatus.status === 'completed' ? '#388E3C' : '#1976d2'}>
                  {batchStatus.status === 'completed'
                    ? `All ${batchStatus.total} calls done`
                    : `Called ${batchStatus.current} of ${batchStatus.total}${batchStatus.next_call_at ? ` • Next ~${batchStatus.next_call_at}` : ''}`}
                </Typography>
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendEmails}
              disabled={interviewData.length === 0 || !timeSlotsAvailable}
              sx={{ minWidth: 160, fontWeight: 600 }}
            >
              {timeSlotsAvailable ? 'Send Email' : 'No time slots available'}
            </Button>
          </Box>
        </Box>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading candidates...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)', overflow: 'hidden', backgroundColor: '#fff', mb: 2 }}>
            <CustomTableContainer component={Paper} elevation={0} sx={{ boxShadow: 'none', borderRadius: 2 }}>
              <Table>
                <CustomTableHead style={{ position: 'sticky', top: 0, backgroundColor: '#202224', zIndex: 1 }}>
                  <TableRow>
                    <TableCell style={{ color: '#fff', minWidth: '180px' }}>Candidate Name</TableCell>
                    <TableCell style={{ color: '#fff', minWidth: '180px' }}>Rank</TableCell>
                    <TableCell style={{ color: '#fff', minWidth: '180px' }}>Resume Score</TableCell>
                    <TableCell style={{ color: '#fff', minWidth: '180px' }}>URL</TableCell>
                    <TableCell style={{ color: '#fff' }}>ChatBot</TableCell>
                  </TableRow>
                </CustomTableHead>
                <TableBody>
                  {interviewData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No candidates found
                      </TableCell>
                    </TableRow>
                  )}
                  {interviewData.map((candidate, index) => (
                    <TableRow key={index}>
                      <TableCell
                        style={{
                          fontFamily: 'Montserrat',
                          fontSize: '14px',
                          fontWeight: 700,
                          lineHeight: '17.07px',
                          textAlign: 'left',
                          color: '#202224',
                          gap: '0px',
                          opacity: 0.6,
                        }}
                      >
                        {candidate['Candidate name']}
                      </TableCell>
                      <TableCell>{candidate.rank}</TableCell>
                      <TableCell>
                        {candidate['Total Resume score']
                          ? candidate['Total Resume score'].toFixed(2)
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <a
                          href={`/${candidate.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Book Slot
                        </a>
                      </TableCell>
                      <TableCell>
                        <a
                          href={`/${candidate.ChatBot}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          InterviewBot
                        </a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CustomTableContainer>
          </Box>
        )}
      </Paper>
      {/* Success message after sending emails */}
      {EmailsSent && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
          <Typography variant="h5">
            Emails sent successfully to selected candidates!
          </Typography>
          <Tooltip title="View Selected Candidates Final Rankings " arrow placement="right">
            <Button
              variant="contained"
              color="primary"
              onClick={handleViewRankings}
              sx={{ mt: 2 }}
            >
              View Rankings
            </Button>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default Interview;
